import { useFormContext, useWatch } from 'react-hook-form';
import { OwnerType, TImportOwnerList, TOwner } from '../../types';
import { Box, Button, Typography } from '@mui/material';
import { styles } from '../../styles';
import { Info } from '@mui/icons-material';
import { useState } from 'react';
import ImportOwnerCard from './ImportOwnerCard';
import { useAppDispatch } from '~/store/appDispatch';
import { updateFormData } from '~/store/identification-form/slices';
import { IdentificationFormStepNames } from '~/modules/identification/types';
import { cloneDeep } from 'lodash';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal.tsx';

export const ImportOwnersModal = () => {
  const dispatch = useAppDispatch();
  const { setValue, getValues, control } = useFormContext();
  const owners = useWatch({ control, name: 'owners' });
  const [isSubmited, setIsSubmited] = useState(false);
  const [ownerTypeValues, setOwnerTypeValues] = useState<TImportOwnerList>();

  const isModalShown = owners.some(
    (owner: TOwner) =>
      !owner.type && (owner.firstName || owner.lastName || owner.middleName || owner.fullName)
  );

  if (!isModalShown) return null;

  const handleUpdateState = () => {
    dispatch(
      updateFormData({
        field: IdentificationFormStepNames.ownershipStructure,
        data: cloneDeep(getValues()),
      })
    );
  };

  const handleCancelImport = () => {
    setValue('owners', [], { shouldDirty: true });
    setValue('link', [], { shouldDirty: true });
    handleUpdateState();
  };

  const handleContinue = () => {
    const keys = Object.keys(ownerTypeValues || {});
    setIsSubmited(true);
    if (keys.some((key) => !ownerTypeValues?.[+key]?.value)) return;
    keys.forEach((key) => {
      const currentValue = ownerTypeValues?.[+key]?.value as OwnerType;
      setValue(`owners.${key}.type`, currentValue, { shouldDirty: true });
      if ([OwnerType.FREE_FLOAT, OwnerType.SHAREHOLDER_GROUP].includes(currentValue)) {
        setValue(`owners.${key}.fullName`, ownerTypeValues?.[+key]?.label, { shouldDirty: true });
      }
      if (OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP === currentValue) {
        setValue(`owners.${key}.isRegardlessOfFormalOwnership`, true, {
          shouldDirty: true,
        });
      }
    });
    handleUpdateState();
  };

  return (
    <BaseModal open={isModalShown} containerProps={{ sx: styles.importModalContainer }}>
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        Імпорт власників з ЄДР
        <Typography>
          Оберіть тип для кожного запису. Записи з ознакою КБВ автоматично визначені як ФО.
        </Typography>
      </Typography>
      <Box sx={styles.importWarningContainer}>
        <Info color={'disabled'} />
        <Typography>
          Скасовуючи імпорт, власники з ЄДР не будуть заповнені. Їх можна додати вручну, натиснувши
          «+ Додати власника».
        </Typography>
      </Box>
      <Box sx={styles.importCardContainer}>
        {owners.map((owner: TOwner, index: number) => (
          <ImportOwnerCard
            index={index}
            owner={owner}
            key={owner.id}
            setOwnerTypeValues={setOwnerTypeValues}
            values={ownerTypeValues}
            isSubmited={isSubmited}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={handleCancelImport} data-testid="cancel-import-button">
          Скасувати імпорт
        </Button>
        <Button variant="contained" onClick={handleContinue} data-testid="confirm-import-button">
          Підтвердити
        </Button>
      </Box>
    </BaseModal>
  );
};
